import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Heart } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useUiText } from "@/contexts/UiTextContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * PO+ read-only Praise Report — testimonies grouped by month, filtered by
 * campus/year/month. Includes a branded PDF export button.
 */
export default function PraiseReport() {
  const { t } = useUiText();
  const { backendUrl } = useAuth();
  const [data, setData] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [campus, setCampus] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (campus) params.campus_id = campus;
      if (year) params.year = year;
      if (month) params.month = month;
      const [r, c] = await Promise.all([
        api.get("/faith-journey/testimonies/praise-report", { params }),
        api.get("/campuses"),
      ]);
      setData(r.data);
      setCampuses(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load Praise Report.");
    }
  };
  useEffect(() => { load(); }, [campus, year, month]);

  const exportPdf = async () => {
    const qs = new URLSearchParams();
    if (campus) qs.set("campus_id", campus);
    if (year) qs.set("year", year);
    if (month) qs.set("month", month);
    const url = `${backendUrl}/api/faith-journey/testimonies/praise-report/export.pdf?${qs.toString()}`;
    try {
      const resp = await fetch(url, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "praise-report.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Praise Report downloaded.");
    } catch {
      toast.error("PDF download failed.");
    }
  };

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2, now - 3];
  }, []);

  return (
    <div className="space-y-6" data-testid="praise-report-page">
      <section className="patria-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
              <Heart className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                People Operations · Read-Only
              </p>
              <h1 className="text-3xl font-serif text-forest-900">
                {t("praise_report.title", "Praise Report")}
              </h1>
              <p className="text-xs text-charcoal-muted mt-1 max-w-2xl">
                {t("praise_report.subtitle", "Testimonies Aggregated By Month + Campus.")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={exportPdf}
            className="patria-btn-primary text-sm"
            data-testid="btn-praise-report-export"
          >
            <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Export PDF
          </button>
        </div>
      </section>

      <section className="patria-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="patria-label">Campus</label>
          <select className="patria-input" value={campus} onChange={(e) => setCampus(e.target.value)} data-testid="pr-filter-campus">
            <option value="">All Campuses</option>
            {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="patria-label">Year</label>
          <select className="patria-input" value={year} onChange={(e) => setYear(e.target.value)} data-testid="pr-filter-year">
            <option value="">All Years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="patria-label">Month</label>
          <select className="patria-input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="pr-filter-month">
            <option value="">All Months</option>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>{new Date(2000, i, 1).toLocaleString(undefined, { month: "long" })}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-charcoal-muted">
          <span data-testid="pr-total-count">{data?.total ?? 0}</span> testimonies matched
        </div>
      </section>

      <div className="space-y-4">
        {(!data || data.groups.length === 0) && (
          <div className="patria-card p-8 text-center text-charcoal-muted italic" data-testid="pr-empty-state">
            No testimonies match yet — a beautiful blank page waiting to be filled.
          </div>
        )}
        {data?.groups.map((g) => (
          <section key={g.month} className="patria-card p-6" data-testid={`pr-group-${g.month}`}>
            <h2 className="font-serif text-2xl text-terracotta-600">
              {g.month === "0000-00" ? "Undated" : g.month}
            </h2>
            <ul className="mt-3 space-y-3">
              {g.testimonies.map((t2) => (
                <li key={t2.id} className="p-4 rounded-2xl bg-cream-100">
                  <p className="italic text-forest-900">&ldquo;{t2.text}&rdquo;</p>
                  <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-2">
                    — {t2.user_name}
                    {t2.user_campus_name ? ` · ${t2.user_campus_name}` : ""}
                    {" · "}{t2.dated_on || "undated"}
                    {t2.context && t2.context !== "general" && ` · ${t2.context.replaceAll("_", " ")}`}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
